import { useState } from 'react'
import { Helmet } from 'react-helmet'

import { Badge, type BadgeVariant } from '@/components/ui/badge'
import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { apiErrorDetail } from '@/lib/apiError'
import { formatDateTime } from '@/lib/formatters'
import {
  CorgiPortalCheckOutcome,
  CorgiPortalSessionState,
  useCheckCorgiPortal,
  useConnectCorgiPortal,
  useCorgiPortalSession,
} from '@/models/corgiPortalSession'

const PORTAL_URL = 'https://trucking.corgi.insure/login'

const STATE_BADGE: Record<CorgiPortalSessionState, { label: string; variant: BadgeVariant }> = {
  [CorgiPortalSessionState.Valid]: { label: 'Connected', variant: 'success' },
  [CorgiPortalSessionState.NeedsHuman]: { label: 'Needs sign-in', variant: 'warning' },
  [CorgiPortalSessionState.Unknown]: { label: 'Not connected', variant: 'outline' },
}

const CHECK_NOTICE: Partial<Record<CorgiPortalCheckOutcome, { variant: 'info' | 'warning'; text: string }>> =
  {
    [CorgiPortalCheckOutcome.Refreshed]: {
      variant: 'info',
      text: 'Corgi accepted a fresh sign-in just now. The session works.',
    },
    [CorgiPortalCheckOutcome.Unavailable]: {
      variant: 'warning',
      text: 'The Corgi portal could not be reached. The session was left as it was; try again in a minute.',
    },
    [CorgiPortalCheckOutcome.Locked]: {
      variant: 'info',
      text: 'Cai is refreshing the session right now. Try again in a moment.',
    },
  }

export function CorgiConnection() {
  const { data: session, isPending, error } = useCorgiPortalSession()
  const connectMutation = useConnectCorgiPortal()
  const checkMutation = useCheckCorgiPortal()
  const [handoff, setHandoff] = useState('')

  function submit() {
    connectMutation.mutate(handoff, { onSuccess: () => setHandoff('') })
  }

  const badge = session ? STATE_BADGE[session.state] : null
  const checkNotice = checkMutation.data ? CHECK_NOTICE[checkMutation.data.outcome] : undefined

  return (
    <>
      <Helmet>
        <title>Corgi connection</title>
      </Helmet>
      <div className="flex max-w-3xl flex-col gap-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">Corgi connection</h1>
          <p className="text-muted-foreground text-sm">
            The Corgi portal login Cai uses to file endorsements. Corgi has no API for them, and its sign-in
            page asks for a CAPTCHA, so a person signs in and hands the session over here. Cai keeps it alive
            from then on.
          </p>
        </div>

        {error && <Banner variant="error">Could not load the connection: {(error as Error).message}</Banner>}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              Status
              {badge && <Badge variant={badge.variant}>{badge.label}</Badge>}
            </CardTitle>
            <CardAction>
              <Button
                variant="outline"
                disabled={!session}
                loading={checkMutation.isPending ? 'Checking…' : undefined}
                onClick={() => checkMutation.mutate()}
              >
                Check now
              </Button>
            </CardAction>
          </CardHeader>
          <CardContent className="flex flex-col gap-1 text-sm">
            {isPending && <p className="text-muted-foreground">Loading…</p>}
            {checkMutation.isError && (
              <Banner variant="error">
                {apiErrorDetail(checkMutation.error, 'Could not check the connection.')}
              </Banner>
            )}
            {checkNotice && <Banner variant={checkNotice.variant}>{checkNotice.text}</Banner>}
            {session?.state === CorgiPortalSessionState.NeedsHuman && (
              <Banner variant="warning">
                Corgi rejected the stored session, so endorsements are on hold. Sign in to the portal again
                and paste a fresh request below.
              </Banner>
            )}
            {session?.accountEmail && (
              <p>
                Signed in as{' '}
                {session.accountName
                  ? `${session.accountName} (${session.accountEmail})`
                  : session.accountEmail}
              </p>
            )}
            {session?.lastRefreshedAt && (
              <p className="text-muted-foreground">
                Last refreshed {formatDateTime(session.lastRefreshedAt)}
                {session.lastRefreshedBy ? ` · connected by ${session.lastRefreshedBy}` : ''}
              </p>
            )}
            {session && session.state === CorgiPortalSessionState.Unknown && !session.accountEmail && (
              <p className="text-muted-foreground">No session yet. Hand one over below.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Hand over a session</CardTitle>
            <CardDescription>
              <ol className="list-decimal space-y-1 pl-5">
                <li>
                  Sign in at{' '}
                  <a className="underline" href={PORTAL_URL} target="_blank" rel="noreferrer">
                    trucking.corgi.insure
                  </a>{' '}
                  with the shared engineering login.
                </li>
                <li>Open DevTools, go to the Network tab and reload the page.</li>
                <li>
                  Right-click any request to <code>api.trucking.corgi.insure</code> and choose Copy → Copy as
                  cURL.
                </li>
                <li>
                  Paste it below. The tab you copied from may ask you to sign in again afterwards; that is
                  expected.
                </li>
              </ol>
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <Label htmlFor="corgi-handoff">Copied request</Label>
              <Textarea
                id="corgi-handoff"
                rows={6}
                spellCheck={false}
                value={handoff}
                onChange={(event) => setHandoff(event.target.value)}
                placeholder="curl 'https://api.trucking.corgi.insure/api/auth/me' …"
              />
            </div>
            {connectMutation.isError && (
              <Banner variant="error">
                {apiErrorDetail(connectMutation.error, 'Could not connect to Corgi.')}
              </Banner>
            )}
            {connectMutation.isSuccess && !handoff && (
              <Banner variant="info">Connected. Cai will keep this session alive from here.</Banner>
            )}
            <div>
              <Button
                onClick={submit}
                disabled={!handoff.trim()}
                loading={connectMutation.isPending ? 'Connecting…' : undefined}
              >
                Connect
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
